import { useEffect, useState } from 'react';

import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

import { getPubSubClient } from './client.ts';
import { eventToAvi, getCurrentProtocolType, type PubSubClient } from './PubSubClient.ts';

type UsePubSubEventProps = {
	contentId: string;
	contentType: string;
	eventName: string;
	onEvent: (...args: any[]) => void;
	attribution?: Attribution;
	skip?: boolean;
};

export const errorStringsToIgnore: string[] = [
	'401',
	'Failed to fetch',
	'Load Failed',
	'Network error',
];

const isIgnoredPubSubError = (error: any) => {
	return errorStringsToIgnore.some((errorString) => error?.message.includes(errorString));
};

// Note: export for testing purposes only
export const getEventHandler = (eventName: string, onEvent: any) => {
	return (...args: any[]): void => {
		onEvent(...args);
		getAnalyticsWebClient().then(
			(client) => {
				client.sendOperationalEvent({
					action: 'received',
					actionSubject: 'pubsub.event',
					actionSubjectId: eventName,
					source: 'usePubSubEvent',
				});
			},
			() => {},
		);
	};
};

/**
 * @deprecated
 * This hook is deprecated and will be removed in the future.
 * Please plan to use AGG and subscriptions instead.
 * @see https://hello.atlassian.net/wiki/spaces/CED/pages/5151690101/Sunset+Frontend+Pubsub+for+GraphQL+Gateway
 */
export const usePubSubEvent = ({
	contentId,
	contentType,
	eventName,
	onEvent,
	attribution,
	skip: skipProp = false,
}: UsePubSubEventProps): void => {
	const [pubSubClient, setPubSubClient] = useState<PubSubClient | undefined>(undefined);
	const { accessStatus } = useSessionData();
	// if user is anonymous, skip
	const skip = accessStatus === AccessStatus.ANONYMOUS_ACCESS || skipProp;

	useEffect(() => {
		if (skip || !contentId || !contentType) {
			return;
		}

		const pubSubPromise = new Promise<PubSubClient>((resolve) => {
			void getPubSubClient().then((psc) => {
				void psc
					.joinChannel(contentType, contentId)
					.then(() => {
						setPubSubClient(psc);

						// Temporarily fire events when PubNub protocol is detected
						// to investigate source of requests
						if (getCurrentProtocolType(psc.client) === 'pubnub') {
							const { analyticsClient, ...config } = (psc.client as any)?.config;
							// copy config obj so we can scrub URL from logs
							const configCopy = JSON.parse(JSON.stringify(config));
							delete configCopy.apsProtocol?.url;
							// eslint-disable-next-line @typescript-eslint/no-floating-promises -- Analytics logging is intentionally fire-and-forget
							getAnalyticsWebClient().then((client) => {
								client.sendOperationalEvent({
									action: 'using.pubnub',
									actionSubject: 'client',
									source: 'usePubSubEvent',
									attributes: {
										contentId,
										contentType,
										config: configCopy,
									},
								});
							});
						}

						resolve(psc);
					})
					.catch((error) => {
						if (!isIgnoredPubSubError(error)) {
							const e = new Error(
								`PubSub (${getCurrentProtocolType(
									psc.client,
								)}) - failed to join channel - ${error} - ${eventName}`,
							);
							getMonitoringClient().submitError(e, {
								attribution: attribution || Attribution.PAGE_EXTENSIONS,
							});
						}
					});
			});
		});

		return () => {
			void pubSubPromise.then((psc) => {
				setPubSubClient(undefined);
				void psc.leaveChannel(contentType, contentId);
			});
		};
	}, [contentType, contentId, attribution, skip, eventName]);

	useEffect(() => {
		if (!pubSubClient) {
			return;
		}

		const avi = eventToAvi(eventName);
		const eventHandler = getEventHandler(avi, onEvent);
		try {
			pubSubClient.subscribeSync(avi, eventHandler);
		} catch (error) {
			const e = new Error(
				`PubSub (${getCurrentProtocolType(
					pubSubClient.client,
				)}) - failed to subscribe to ${eventName} - ${error}`,
			);
			getMonitoringClient().submitError(e, {
				attribution: attribution || Attribution.PAGE_EXTENSIONS,
			});
		}

		return () => {
			pubSubClient.unsubscribeSync(avi, eventHandler);
		};
	}, [pubSubClient, eventName, onEvent, attribution]);
};
